import { createFileRoute, notFound } from "@tanstack/react-router";
import { ensureExists } from "@/lib/ensureExists";
import { productHubSlugs } from "@/lib/productHubConfig";
import { buyersGuides } from "@/lib/buyersGuideConfig";
import ProductHub from "@/pages/ProductHub";
import AmazonBuyersGuide from "@/pages/AmazonBuyersGuide";
import Page from "@/pages/Page";
import { hubArticlesQueryOptions } from "@/lib/hubArticles";
import { pageQueryOptions } from "@/lib/pageQuery";
import { getSearchDescription } from "@/lib/searchDescriptions";

const SlugIndex = () => {
  const { slug } = Route.useParams();
  if (productHubSlugs.includes(slug)) return <ProductHub hubSlug={slug} />;
  const guideKey = slug.match(/^best-(.+)-uk$/)?.[1];
  if (guideKey && guideKey in buyersGuides) return <AmazonBuyersGuide />;
  return <Page />;
};

export const Route = createFileRoute("/$slug/")({
  beforeLoad: async ({ params }) => {
    const slug = params.slug;
    if (productHubSlugs.includes(slug)) return;
    const guideKey = slug.match(/^best-(.+)-uk$/)?.[1];
    if (guideKey && guideKey in buyersGuides) return;
    await ensureExists("pages", slug);
  },
  loader: async ({ params, context }) => {
    if (productHubSlugs.includes(params.slug)) {
      await context.queryClient.ensureQueryData(hubArticlesQueryOptions(params.slug)).catch(() => null);
      return { page: null };
    }
    const guideKey = params.slug.match(/^best-(.+)-uk$/)?.[1];
    if (guideKey && guideKey in buyersGuides) return { page: null };
    const page = await context.queryClient.ensureQueryData(pageQueryOptions(params.slug));
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page;
    if (!page) return {};
    const url = `https://www.garden-review.co.uk/${page.slug}`;
    const title = `${page.meta_title || page.title} | Garden-Review`;
    const description = getSearchDescription(`/${page.slug}`, page.meta_description || page.excerpt ||
      (page.content || "").replace(/<[^>]*>/g, " ").replace(/[#*_`]/g, "").replace(/\s+/g, " ").trim().slice(0, 160));
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SlugIndex,
});
